/**
 * The country's auto-send as the header's tile reads it (19/10/2026): one
 * word for the state ("Off", "Live", "Simulated", "Halted", "Stopped"), a detail
 * line (how many campaign groups send, today's sends) and a status (the
 * next send, or who sends) — the same facts as the old sentence
 * (countryFact), cut for a tile.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenGroupSendStatus, DropGenSchedulerPlanStatus, DropGenServerSendModes } from '@trafficpulse/shared';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { FactStatus, FactTone } from '../facts/generation-fact';
import { type AutoSendTone, clockIn, n, nextOf, serverSendsNothing, simulatedNote } from './autosend-fact';

export interface AutoSendTile {
  tone: AutoSendTone;
  value: string;
  detail: string;
  status: FactStatus;
}

const tile = (tone: AutoSendTone, value: string, detail: string, status: [string, FactTone]): AutoSendTile => ({ tone, value, detail, status: { text: status[0], tone: status[1] } });

export function countryTile(plan: DropGenPlanDetail, status: DropGenSchedulerPlanStatus | undefined, groups: DropGenGroupSendStatus[], server: DropGenServerSendModes | undefined): AutoSendTile {
  if (plan.killSwitch) return tile('blocked', 'Stopped', 'The kill switch is on', ['Nothing sends', 'warning']);
  if (!plan.enabled) return tile('blocked', 'Stopped', 'The plan is disabled', ['Nothing sends', 'muted']);
  if (plan.autoSendHaltKind === 'TECHNICAL') return tile('stuck', 'Halted', 'A send needs a person', ['Waiting for a person', 'warning']);
  if (groups.length === 0) return tile('off', 'Off', 'No campaign group yet', ['You send', 'muted']);
  const on = groups.filter((g) => g.config.autoSend);
  const total = `${groups.length} campaign group${groups.length === 1 ? '' : 's'}`;
  if (on.length === 0) return tile('off', 'Off', total, ['You send', 'muted']);
  const share = groups.length > 1 ? `${on.length} of ${groups.length} groups` : null;
  if (on.some((g) => !g.config.shadow) && serverSendsNothing(server)) return tile('blocked', 'Live', share ?? total, ['This server never sends', 'muted']);
  return sendingTile(on, status, share, server);
}

function sendingTile(on: DropGenGroupSendStatus[], status: DropGenSchedulerPlanStatus | undefined, share: string | null, server: DropGenServerSendModes | undefined): AutoSendTile {
  const real = on.filter((g) => !g.config.shadow).length;
  const halted = on.filter((g) => g.haltedToday).length;
  const parts = share ? [share] : [];
  if (real > 0) parts.push(`${n(status?.sendToday?.sentDrops ?? 0)} sent today`);
  if (real < on.length) parts.push(`${n(status?.sendToday?.shadowDrops ?? 0)} would have sent`);
  if (halted > 0 && halted < on.length) parts.push(`${halted} halted`);
  if (halted === on.length) return tile('halted', 'Halted', parts.join(' · '), ['Halted for today — stop-loss', 'warning']);
  const next = nextOf(on.filter((g) => !g.haltedToday));
  const sim = simulatedNote(on.filter((g) => !g.config.shadow), server);
  const line: [string, FactTone] = next ? [`Next send ${clockIn(next, 'Europe/Copenhagen')} 🇩🇰${sim}`, 'live'] : [`No send waiting${sim}`, 'muted'];
  return tile(real === 0 ? 'shadow' : 'on', real === 0 ? 'Simulated' : 'Live', parts.join(' · '), line);
}
